<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useAsyncData, useRuntimeConfig } from '#imports'
import { useHead } from '#imports'
import { useScreen } from '@/composables/useScreen'

const axios = useAxios()
const { loadSession, status, user } = useSession()
const isConfirmEmailLabelVisible = ref(false)
const { isMobileView } = useScreen()
const { t } = useI18n()

const lawyerSearchType = ref('smart_type')

const FaqItems = [
  {
    title: t('faq_what_platform_offers'),
    description: t('faq_what_platform_offers_description')
  },
  {
    title: t('faq_how_to_choose_right_lawyer'),
    description: t('faq_how_to_choose_right_lawyer_description')
  },
  {
    title: t('faq_how_to_schedule_meeting'),
    description: t('faq_how_to_schedule_meeting_description')
  },
  {
    title: t('faq_is_first_meeting_charged'),
    description: t('faq_is_first_meeting_charged_description')
  },
  {
    title: t('faq_why_fill_details'),
    description: t('faq_why_fill_details_description')
  },
  {
    title: t('faq_not_happy_after_meeting'),
    description: t('faq_not_happy_after_meeting_description')
  }
]

// Check email confirmation on client side
useAsyncData('isConfirmEmailLabelVisible', async () => {
  if (process.client && status.value === 'authenticated') {
    await loadSession()
    if (user.value?.email_confirmed === false) {
      isConfirmEmailLabelVisible.value = true
    }
  }
  return null
})

// Load recommended lawyers
const { isReady: areRecommendedLawyersLoaded, state: recommendedLawyersList } = useAsyncState(
  async () => {
    const specialty = t('advocates')
    const { data } = await axios.get('/lawyers', {
      params: { specialty, limit: 6, sorting: 'ourRecommended' }
    })
    // Ensure we have an array
    if (!Array.isArray(data)) {
      console.error('Expected array from lawyers API, got:', data)
      return []
    }
    return data
  },
  [],
  {
    onError: (error) => console.log(error)
  },
)

// ------------------ SEO Improvements ------------------
const config = useRuntimeConfig()

// Page <title> / <meta> description
const pageTitle = computed(() => t('find_the_best_lawyers_easily_and_quickly'))
const metaDescription = computed(() => t('register_with_the_desired_lawyer_description'))

// Canonical link
const canonicalLink = computed(() => {
  // If the site’s baseURL is the true canonical for the homepage:
  return config.public.baseURL.includes('localhost')
    ? 'https://geriteisininkai.lt/'
    : config.public.baseURL + '/'
})

// JSON-LD for a main "WebPage" type
const structuredData = computed(() => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle.value,
    "url": canonicalLink.value,
    "description": metaDescription.value
  }
})

// Insert into <head>
useHead({
  title: pageTitle.value,
  meta: [
    {
      name: 'description',
      content: metaDescription.value
    },
    {
      property: 'og:title',
      content: pageTitle.value
    },
    {
      property: 'og:description',
      content: metaDescription.value
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalLink.value
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: computed(() => JSON.stringify(structuredData.value))
    }
  ]
})
</script>

<template>
  <div class="z-[50]">

    <div class="grid -mt-18 lg:mt-0 lg:container lg:mx-auto lg:mb-18">
      <div v-if="isConfirmEmailLabelVisible" class="info-block-green mb-4 mt-4 text-xl text-gray-800">
        {{ $t('we_have_sent_you_a_confirmation_email_full') }}
      </div>
      <div class="relative grid place-items-center min-h-dvh lg:min-h-0">
        <picture class="absolute inset-0 bg-darkBlue lg:relative lg:bg-darkBlue lg:rounded-[40px] overflow-hidden w-full h-full">
          <!-- Desctop view image -->
          <source srcset="@/assets/images/landingPageMainImage.jpg" media="(min-width: 900px)" />
          <!-- Mobile fallback -->
          <img src="@/assets/images/landingPageMainImageMobile.jpg" alt="Landing background" class="opacity-50 object-cover w-full h-full object-center" />
        </picture>

        <h1 class="absolute font-redhat font-semibold lg:font-bold text-white z-100 max-w-[327px] lg:max-w-full lg:w-[550px] xl:w-[680px] 2xl:w-[788px] text-4xl lg:text-[40px] xl:text-[52px] 2xl:text-60 leading-tight lg:leading-[1.10] transition-all duration-[750ms]"
          :class="lawyerSearchType === 'smart_type'? 'bottom-2/3 xl:bottom-[calc(50%+10px)]' : 'bottom-2/3 lg:bottom-1/2'"
        >
          {{ $t('find_the_best_lawyers_easily_and_quickly') }}
        </h1>

        <div class="absolute text-white w-full z-100 bottom-5 px-[5%] transition-all duration-[750ms]" :class="lawyerSearchType === 'smart_type'? 'lg:px-[10%] lg:bottom-6': 'lg:px-[5%] lg:bottom-[6%] xl:bottom-[8%] 2xl:bottom-[11%]'">
          <ServicesPanel @update:lawyerSearchType="(newValue) => lawyerSearchType = newValue" />
        </div>
      </div>
    </div>

    <div class="lg:border-t lg:border-indigo-50 bg-gray pt-18 lg:pt-0 pb-9 lg:pb-20">
      <div class="lg:container mx-6">
        <div class="lg:mt-20 pb-12 font-redhat font-semibold text-darkBlue text-36 lg:text-60 flex lg:block items-center justify-center">
          {{ $t('what_we_offer') }}
        </div>
        <what-we-offer />
      </div>
    </div>

    <how-it-works class="lg:my-20" />

    <div v-if="areRecommendedLawyersLoaded" class="py-9 lg:py-0 lg:mt-20 lg:container">
      <div class="mx-6 lg:mx-0">
        <div class="flex justify-center text-darkBlue font-redhat font-semibold text-36 lg:text-60">
          {{ $t('top_rated_lawyers') }}
        </div>
        <div class="flex justify-center text-darkBlue font-redhat font-medium text-md mt-4 mb-6">
          {{ $t('top_rated_lawyers_description') }}
        </div>
        <div class="flex items-center justify-center">
          <NuxtLinkLocale
            :to="{ name: 'lawyers-specialty', params: { specialty: $t('advocates').toLowerCase() }}"
            class="py-3 px-6 h-12 w-52 rounded-full mb-10 lg:mb-14 text-md text-white bg-darkBlue inline-flex items-center justify-center"
          >
            {{ $t('search_lawyer') }}
          </NuxtLinkLocale>
        </div>
        <recommended-lawyers :recommended-lawyers-list="recommendedLawyersList"/>
      </div>
    </div>

    <div class="border-t border-indigo-50 bg-gray pb-20">
      <div class="container">
        <div class="lg:flex">
          <div class="mt-18 lg:pb-12 lg:mr-3 font-redhat font-semibold text-darkBlue text-36 lg:text-60 text-center">
            {{ $t('lawyers_by_specialisation') }}
          </div>
          <div class="flex items-center justify-center mt-3 lg:mt-0 lg:ml-auto">
            <NuxtLinkLocale
              :to="{ name: 'lawyers-specialty', params: { specialty: $t('advocates').toLowerCase() }}"
              class="py-3 px-6 h-12 w-52 min-w-52 rounded-full mt-4 lg:mt-8 mb-10 lg:mb-0 lg:mr-9 text-md text-white bg-darkBlue inline-flex items-center justify-center"
            >
              {{ $t('all_lawyers') }}
            </NuxtLinkLocale>
          </div>
        </div>
        <lawyers-by-specialisations />
      </div>
    </div>

    <what-our-customers-say />

    <div id="contactUs" class="my-0 lg:py-20">
      <contact-us />
    </div>

    <FAQ :items="FaqItems" />

  </div>
</template>
